import { Button, ButtonProps } from "@mui/material";
import { isNil } from "lodash-es";
import { ForwardedRef, forwardRef, useState } from "react";

import AppLink from "../Link/AppLink";
import AppTooltip, { AppTooltipProps } from "../Tooltip/AppTooltip";

export type AppButtonProps = ButtonProps & {
  /** If set, link is used as component. */
  to?: string;
  tooltipProps?: Omit<AppTooltipProps, "children">;
  /** Whether to auto-track loading state based on Promise returned by onClick.
   * @default true
   */
  isAutoTrackLoading?: boolean;
  /** Disable button on loading state instead of showing loading indicator. */
  isDisabledOnLoading?: boolean;
  /** If Promise returned, loading is auto-set to true. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
};

// // forwardRef with TS generic component trick:
// // https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
// type TheButtonWithForwardedRefType = <
//   RootComponent extends React.ElementType = ButtonTypeMap["defaultComponent"],
//   // eslint-disable-next-line @typescript-eslint/no-empty-object-type
//   AdditionalProps = {},
// >(
//   props: AppButtonProps<RootComponent, AdditionalProps> & {
//     ref?: ForwardedRef<HTMLButtonElement>;
//   },
// ) => ReturnType<typeof TheButton>;

/** Extends MUI Button. */
export default forwardRef<HTMLButtonElement, AppButtonProps>(function TheButton(
  {
    // component,
    loading,
    tooltipProps,
    isAutoTrackLoading = true,
    isDisabledOnLoading,
    to,
    sx,
    children,
    onClick,
    ...otherProps
  }: AppButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingComputed = loading ?? isLoading;

  return (
    <AppTooltip
      isEnabled={!isNil(tooltipProps)}
      title={tooltipProps?.title || ""}
      {...tooltipProps}
    >
      <Button
        ref={ref}
        sx={{
          ...sx,
        }}
        component={to ? AppLink : undefined}
        // @ts-expect-error Expected error as component is conditionally set.
        to={to || undefined}
        {...otherProps}
        disabled={isAutoTrackLoading && isDisabledOnLoading && isLoadingComputed}
        loading={isAutoTrackLoading && !isDisabledOnLoading && isLoadingComputed}
        onClick={
          onClick
            ? async (e) => {
                setIsLoading(true);
                try {
                  await onClick(e);
                } finally {
                  setIsLoading(false);
                }
              }
            : undefined
        }
      >
        {children}
      </Button>
    </AppTooltip>
  );
});

// const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
//   TheButton,
// ) as TheButtonWithForwardedRefType;

// export default AppButton;
